import React, { useState } from 'react';
import { StationSector } from '../../../../../../utils/types';
import { FormSection } from '../../../../common';
import ShareIcon from '@mui/icons-material/Share';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';

interface SectorCodeshareSectionProps {
  station: StationSector;
  sectorIndex: number;
  onUpdate: (key: keyof StationSector, value: StationSector[keyof StationSector]) => void;
}

/**
 * Form section for codeshare information at the sector level
 * Each sector can have different codeshare partners
 */
export function SectorCodeshareSection({
  station,
  sectorIndex,
  onUpdate,
}: SectorCodeshareSectionProps) {
  const [codeshareOwner, setCodeshareOwner] = useState('');
  const [codeshareNumber, setCodeshareNumber] = useState('');

  const handleAdd = () => {
    const owner = codeshareOwner.trim();
    const number = codeshareNumber.trim();

    if (!owner || !number) return;

    const fullFlight = `${owner} ${number}`;
    const currentCodeshares = station.codeshareFlightNo || [];

    // Avoid duplicates
    if (currentCodeshares.includes(fullFlight)) {
      setCodeshareOwner('');
      setCodeshareNumber('');
      return;
    }

    onUpdate('codeshareFlightNo', [...currentCodeshares, fullFlight]);
    setCodeshareOwner('');
    setCodeshareNumber('');
  };

  const handleRemove = (index: number) => {
    const currentCodeshares = station.codeshareFlightNo || [];
    const updated = currentCodeshares.filter((_, idx) => idx !== index);
    onUpdate('codeshareFlightNo', updated);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  const handleOwnerChange = (value: string) => {
    // Allow only uppercase letters, max 3 characters
    const filtered = value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3);
    setCodeshareOwner(filtered);
  };

  const handleNumberChange = (value: string) => {
    // Allow only digits, max 4 characters
    const filtered = value.replace(/[^0-9]/g, '').slice(0, 4);
    setCodeshareNumber(filtered);
  };

  const codeshares = station.codeshareFlightNo || [];
  const sector = `${station.departureStation} → ${station.arrivalStation}`;

  return (
    <FormSection
      title={`Codeshare Information (${sector})`}
      icon={<ShareIcon sx={{ fontSize: 20 }} />}
      collapsible
      defaultExpanded={true}
    >
      <div className="space-y-4">
        {/* Input Row */}
        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <label className="block mb-1.5 text-base font-semibold text-gray-600">
              Codeshare Flight Number
            </label>
            <div className="flex gap-2">
              {/* Flight Owner */}
              <input
                type="text"
                value={codeshareOwner}
                onChange={e => handleOwnerChange(e.target.value)}
                onKeyPress={handleKeyPress}
                maxLength={3}
                className="w-20 px-3 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 text-lg outline-none transition-all
                  focus:border-primary-500 focus:ring-3 focus:ring-primary-500/10 font-mono text-center"
              />
              {/* Flight Number */}
              <input
                type="text"
                value={codeshareNumber}
                onChange={e => handleNumberChange(e.target.value)}
                onKeyPress={handleKeyPress}
                maxLength={4}
                className="flex-1 px-3 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 text-lg outline-none transition-all
                  focus:border-primary-500 focus:ring-3 focus:ring-primary-500/10 font-mono"
              />
            </div>
          </div>
          <button
            type="button"
            onClick={handleAdd}
            disabled={!codeshareOwner.trim() || !codeshareNumber.trim()}
            className="px-4 py-2.5 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600
              disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            <AddIcon sx={{ fontSize: 18 }} />
            Add
          </button>
        </div>

        {/* Display added codeshares */}
        {codeshares.length > 0 && (
          <div>
            <p className="text-sm font-semibold text-gray-600 mb-2">
              Codeshare Flights ({codeshares.length})
            </p>
            <div className="flex flex-wrap gap-2">
              {codeshares.map((flight, idx) => (
                <div
                  key={idx}
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-md"
                >
                  <span className="font-mono font-medium text-sm">{flight}</span>
                  <button
                    type="button"
                    onClick={() => handleRemove(idx)}
                    className="text-blue-700 hover:text-blue-900 hover:bg-blue-100 rounded-full p-0.5 transition-colors"
                    aria-label={`Remove ${flight}`}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {codeshares.length === 0 && (
          <div className="text-sm text-gray-500 italic p-3 bg-gray-50 rounded-lg border border-gray-200">
            No codeshare flights added for this sector. Add marketing flight numbers above.
          </div>
        )}
      </div>
    </FormSection>
  );
}
